import {
  createSlice,
  createAsyncThunk,
  createEntityAdapter,
} from '@reduxjs/toolkit';
import { rejectValue } from 'redux/helpers';
import _compact from 'lodash/compact';
import _isEmpty from 'lodash/isEmpty';
import format from 'date-fns/format';

import { GET_WEBHOOKS, GET_WEBHOOKS_BY_ID } from 'services/api';
import { WEBHOOK, WEBHOOK_ALL, WEBHOOK_BY_ID, WEBHOOK_SEARCH } from './types';
import { initialRange } from 'components/DateRangePicker/ranges';
import { DATE_FOR_API } from 'utils/dateFormat';

const webhookAdapter = createEntityAdapter();

export const initialState = webhookAdapter.getInitialState({
  isLoading: false,
  detailsIsLoading: false,
  pagination: {
    total: 1,
    total_pages: 1,
    current_page: 1,
    per_page: 25,
    count: 1,
  },
  params: {
    per_page: 25,
    from: format(initialRange.startDate, DATE_FOR_API),
    to: format(initialRange.endDate, DATE_FOR_API),
  },
  search: {
    term: '',
    submitted: false,
  },
});

export const getWebhooks = createAsyncThunk(
  WEBHOOK_ALL,
  async (_, { rejectWithValue, getState, dispatch }) => {
    try {
      const companyId = getState().company.current;
      const { data, meta } = await GET_WEBHOOKS({
        ...getState().webhook.params,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      return { data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const getWebhookById = createAsyncThunk(
  WEBHOOK_BY_ID,
  async (id, { rejectWithValue }) => {
    try {
      const { data } = await GET_WEBHOOKS_BY_ID(id);
      return data;
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);

export const searchWebhooks = createAsyncThunk(
  WEBHOOK_SEARCH,
  async (_, { rejectWithValue, getState }) => {
    try {
      const companyId = getState().company.current;
      const { term } = getState().webhook.search;

      if (!term) {
        return;
      }

      const { data, meta } = await GET_WEBHOOKS({
        term,
        company_id: companyId,
      });

      if (!data.length) return { data: [], pagination: meta.pagination };

      return { data, pagination: meta.pagination };
    } catch (error) {
      return rejectWithValue(rejectValue(error.response));
    }
  }
);
const webhookSlice = createSlice({
  name: WEBHOOK,
  initialState,
  reducers: {
    setParams: (state, { payload }) => {
      state.params = { ...state.params, ...payload };
    },
    setSearchTerm: (state, { payload }) => {
      state.search.term = payload;
    },
    resetSearch: (state) => {
      state.search = initialState.search;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getWebhooks.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = false;
      })
      .addCase(getWebhooks.rejected, (state) => {
        state.isLoading = false;
      })
      .addCase(getWebhooks.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        webhookAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addCase(getWebhookById.pending, (state) => {
        state.detailsIsLoading = true;
      })
      .addCase(getWebhookById.rejected, (state) => {
        state.detailsIsLoading = false;
      })
      .addCase(getWebhookById.fulfilled, (state, { payload }) => {
        state.detailsIsLoading = false;
        if (!_isEmpty(payload)) {
          webhookAdapter.upsertOne(state, payload);
        }
      })
      .addCase(searchWebhooks.pending, (state) => {
        state.isLoading = true;
        state.search.submitted = true;
      })
      .addCase(searchWebhooks.rejected, (state) => {
        state.isLoading = false;
        state.search.submitted = false;
      })
      .addCase(searchWebhooks.fulfilled, (state, { payload }) => {
        state.isLoading = false;
        webhookAdapter.setAll(state, payload.data);
        state.pagination = payload.pagination;
      })
      .addDefaultCase((state) => state);
  },
});

export const webhookSelectors = webhookAdapter.getSelectors(
  (state) => state.webhook
);

export const webhookByIdSelector = (state, id) => ({
  data: webhookSelectors.selectById(state, id),
  isLoading: state.webhook.detailsIsLoading,
});
export const webhookAllSelector = (state) => ({
  data: _compact(webhookSelectors.selectAll(state)),
  pagination: state.webhook.pagination,
  isLoading: state.webhook.isLoading,
  params: state.webhook.params,
  searchTerm: state.webhook.search.term,
});
export const { setParams, setSearchTerm, resetSearch } = webhookSlice.actions;

export default webhookSlice.reducer;
